/**
 * LoginPage - Authentication page for user login
 * Full implementation with React Hook Form, validation, and proper error handling
 */

import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import styled from 'styled-components';

import { FormField } from '@components/forms/FormField';
import { Button } from '@components/ui/Button';
import { API_CONFIG } from '@constants';
import { useAuthOperations } from '@hooks/useApi';
import { apiService } from '@services/api';
import { LoginForm } from '@defs/forms';

// ============================================================================
// Validation Schema
// ============================================================================

const loginSchema = yup.object({
  email: yup
    .string()
    .email('Email inválido')
    .required('Email es requerido')
    .trim(),
  password: yup
    .string()
    .required('Contraseña es requerida')
    .min(1, 'Contraseña es requerida'),
});

// ============================================================================
// Styled Components
// ============================================================================

const GoogleButton = styled.a`
  background: var(--surface);
  color: var(--text-primary);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  font-weight: 600;
  text-decoration: none;
  transition: background-color 0.2s ease-in-out;
  width: 100%;
  margin-bottom: 0;

  &:hover {
    background: var(--neutral-25);
    text-decoration: none;
  }

  &:focus-visible {
    outline: 3px solid var(--brand-600);
    outline-offset: 2px;
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

const Divider = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 0.75rem;
  color: var(--text-muted);
  font-size: 0.9rem;
  margin: 1.5rem 0;

  &::before,
  &::after {
    content: '';
    height: 1px;
    background: var(--border);
    display: block;
  }
`;

const ForgotPasswordLink = styled.div`
  text-align: center;
  margin: 1rem 0;

  a {
    color: var(--brand-600);
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }

    &:focus-visible {
      outline: 2px solid var(--brand-600);
      outline-offset: 2px;
      border-radius: 2px;
    }
  }
`;

const FormActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const HelperRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
  font-size: 0.95rem;
  text-align: center;

  a {
    color: var(--brand-600);
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }

    &:focus-visible {
      outline: 2px solid var(--brand-600);
      outline-offset: 2px;
      border-radius: 2px;
    }
  }
`;

const DevPanel = styled.div`
  margin-top: 2rem;
  padding: 1rem;
  background: var(--neutral-25);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  border-left: 4px solid var(--accent-orange-600);

  h3 {
    margin: 0 0 1rem 0;
    color: var(--text-muted);
    font-size: 0.9rem;
    text-align: center;
    font-weight: 600;
  }

  .field-help {
    text-align: center;
    margin-top: 0.75rem;
    font-size: 0.85rem;
    color: var(--text-muted);
  }
`;

// ============================================================================
// Component
// ============================================================================

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, devLogin, isLoading } = useAuthOperations();
  const [recoveryMessage, setRecoveryMessage] = useState('');
  const [recoveryPending, setRecoveryPending] = useState(false);
  const [showDevPanel, setShowDevPanel] = useState(false);

  // Get the intended destination after login
  const from = location.state?.from?.pathname || '/';

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    trigger,
    setFocus,
  } = useForm<LoginForm>({
    resolver: yupResolver(loginSchema),
    mode: 'onBlur',
  });

  // Check if dev mode is available
  React.useEffect(() => {
    const checkDevMode = async () => {
      try {
        const config = await apiService.config();
        setShowDevPanel(config.features?.google_auth || false); // Show dev panel if Google auth is enabled (dev feature)
      } catch {
        setShowDevPanel(false);
      }
    };

    checkDevMode();
  }, []);

  // Handle email/password login
  const onSubmit = async (data: LoginForm) => {
    try {
      await login(data);
      toast.success('¡Bienvenido de vuelta!');
      navigate(from, { replace: true });
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.error('Login error:', error);
      const apiError = error as {
        status?: number;
        detail?: string;
        message?: string;
      };

      // Handle specific error cases
      if (apiError.status === 401) {
        toast.error('Credenciales inválidas. Verifica tu email y contraseña.');
      } else if (apiError.status === 403) {
        toast.error(
          'Cuenta no verificada. Revisa tu email para verificar tu cuenta.',
        );
      } else if (apiError.status === 429) {
        toast.error('Demasiados intentos. Intenta de nuevo más tarde.');
      } else if (apiError.detail) {
        toast.error(apiError.detail);
      } else {
        toast.error('Error al iniciar sesión. Intenta de nuevo.');
      }
    }
  };

  // Handle dev login
  const handleDevLogin = async () => {
    try {
      await devLogin({
        username: 'dev@local',
        password: 'changeme',
      });

      toast.success('¡Acceso de desarrollo exitoso!');
      navigate(from, { replace: true });
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.error('Dev login error:', error);
      toast.error('Error en acceso de desarrollo');
    }
  };

  // Handle forgot password
  const handleForgotPassword = async () => {
    if (recoveryPending) return;
    if (!(await trigger('email'))) {
      setRecoveryMessage(
        'Introduce tu email arriba para solicitar un enlace de recuperación.',
      );
      setFocus('email');
      return;
    }
    setRecoveryPending(true);
    setRecoveryMessage('Solicitando enlace…');
    try {
      await apiService.forgotPassword(getValues('email'));
      setRecoveryMessage(
        'Si existe una cuenta con ese email, te enviaremos instrucciones para recuperar el acceso. Revisa tu correo.',
      );
    } catch {
      setRecoveryMessage('No se pudo solicitar el enlace. Inténtalo de nuevo.');
    } finally {
      setRecoveryPending(false);
    }
  };

  return (
    <>
      <h1 className="auth-title">Iniciar sesión</h1>
      <p className="auth-subtitle">
        Accede para gestionar tus métricas de sostenibilidad
      </p>

      {location.state?.message && (
        <p role="status" className="field-help">
          {location.state.message}
        </p>
      )}
      <div className="results-actions" style={{ marginBottom: '0' }}>
        <GoogleButton
          href={`${API_CONFIG.BASE_URL.replace(/\/?$/, '')}/auth/google`}
          aria-label="Continuar con Google"
          target="_self"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M22.56 12.25C22.56 11.47 22.49 10.72 22.36 10H12V14.26H17.92C17.66 15.63 16.88 16.79 15.71 17.57V20.34H19.28C21.36 18.42 22.56 15.6 22.56 12.25Z"
              fill="#4285F4"
            />
            <path
              d="M12 23C15.24 23 17.95 21.92 19.28 20.34L15.71 17.57C14.74 18.22 13.48 18.62 12 18.62C8.87 18.62 6.22 16.68 5.35 14.04H1.64V16.96C2.96 19.58 7.16 23 12 23Z"
              fill="#34A853"
            />
            <path
              d="M5.35 14.04C5.13 13.39 5 12.7 5 12S5.13 10.61 5.35 9.96V7.04H1.64C0.59 9.13 0 10.51 0 12S0.59 14.87 1.64 16.96L5.35 14.04Z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38C13.62 5.38 15.06 5.94 16.21 7.02L19.36 3.87C17.95 2.61 15.24 1 12 1C7.16 1 2.96 4.42 1.64 7.04L5.35 9.96C6.22 7.32 8.87 5.38 12 5.38Z"
              fill="#EA4335"
            />
          </svg>
          <span>Continuar con Google</span>
        </GoogleButton>
      </div>

      <Divider>o con email</Divider>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormField
          label="Email"
          name="email"
          type="email"
          placeholder="tu@email.com"
          required
          autoComplete="email"
          register={register}
          error={errors.email}
        />

        <FormField
          label="Contraseña"
          name="password"
          type="password"
          placeholder="••••••••"
          required
          autoComplete="current-password"
          register={register}
          error={errors.password}
        />

        <ForgotPasswordLink>
          <button
            type="button"
            onClick={handleForgotPassword}
            disabled={recoveryPending}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--brand-600)',
              textDecoration: 'none',
              fontSize: '0.9rem',
              fontWeight: 500,
              cursor: 'pointer',
              padding: '0.25rem',
            }}
          >
            {recoveryPending
              ? 'Solicitando enlace…'
              : '¿Olvidaste tu contraseña?'}
          </button>
          {recoveryMessage && (
            <p role="status" className="field-help">
              {recoveryMessage}
            </p>
          )}
        </ForgotPasswordLink>

        <FormActions>
          <Button
            type="submit"
            variant="primary"
            size="md"
            fullWidth
            loading={isLoading}
            disabled={isLoading}
          >
            Entrar
          </Button>
        </FormActions>
      </form>

      <HelperRow>
        <span>
          ¿No tienes una cuenta? <Link to="/auth/register">Crear cuenta</Link>
        </span>
      </HelperRow>

      {showDevPanel && (
        <DevPanel>
          <h3>Acceso rápido (DEV)</h3>
          <Button
            variant="secondary"
            size="sm"
            fullWidth
            onClick={handleDevLogin}
            disabled={isLoading}
          >
            Entrar como dev@local
          </Button>
          <div className="field-help">Solo visible en desarrollo.</div>
        </DevPanel>
      )}
    </>
  );
};
